import React, { useCallback, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSelected, useFocused } from 'slate-react';
import { Image, Settings, X, Upload, Link } from 'lucide-react';

// Import static images from assets
import page1chart from '../../../../../assets/images/page1chart.png';
import page2chart from '../../../../../assets/images/page2chart.png';
import page3chart from '../../../../../assets/images/page3chart.png';
import page4chart from '../../../../../assets/images/page4chart.png';
import page5chart from '../../../../../assets/images/page5chart.png';
import mountain from '../../../../../assets/images/mountain.jpeg';

// Static image options
const STATIC_IMAGES = {
  page1chart: { label: 'Executive Summary Word Cloud', src: page1chart },
  page2chart: { label: 'Risk Environment Chart', src: page2chart },
  page3chart: { label: 'Risk Appetite Chart', src: page3chart },
  page4chart: { label: 'Risk Matrix', src: page4chart },
  page5chart: { label: 'Risk Categories', src: page5chart },
  mountain: { label: 'Mountain Background', src: mountain },
};

// Settings Panel Portal - renders outside to avoid clipping
function SettingsPortal({ children, buttonRef }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right + window.scrollX - 350, // Panel width
      });
    }
  }, [buttonRef]);

  return ReactDOM.createPortal(
    <div 
      style={{
        position: 'absolute',
        top: position.top,
        left: Math.max(16, position.left),
        zIndex: 9999,
      }}
    >
      {children}
    </div>,
    document.body
  );
}

function StaticImageElement({ attributes, children, element, onUpdate, onDelete }) {
  const selected = useSelected();
  const focused = useFocused();
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('gallery'); // 'gallery', 'upload', 'url'
  const [isUploading, setIsUploading] = useState(false);
  const settingsButtonRef = useRef(null);
  const fileInputRef = useRef(null);

  const { src, alt = '', width = '100%', alignment = 'center' } = element;

  // Close settings when clicking outside
  useEffect(() => {
    if (showSettings) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.image-settings-panel') && 
            !e.target.closest('.image-settings-button')) {
          setShowSettings(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSettings]);

  // Get the actual image source
  const getImageSrc = () => {
    if (STATIC_IMAGES[src]) {
      return STATIC_IMAGES[src].src;
    }
    // If it's a URL or data URI, use it directly
    if (src && (src.startsWith('http') || src.startsWith('data:'))) {
      return src;
    }
    return null;
  };

  const imageSrc = getImageSrc();

  const handleImageSelect = useCallback((imageKey) => {
    if (onUpdate) {
      onUpdate(element.id, { src: imageKey });
    }
  }, [element.id, onUpdate]);

  const handleAltChange = useCallback((newAlt) => {
    if (onUpdate) {
      onUpdate(element.id, { alt: newAlt });
    }
  }, [element.id, onUpdate]);

  const handleWidthChange = useCallback((newWidth) => {
    if (onUpdate) {
      onUpdate(element.id, { width: newWidth });
    }
  }, [element.id, onUpdate]);

  const handleAlignmentChange = useCallback((newAlignment) => {
    if (onUpdate) {
      onUpdate(element.id, { alignment: newAlignment });
    }
  }, [element.id, onUpdate]);

  const handleCustomUrlChange = useCallback((url) => {
    if (onUpdate) {
      onUpdate(element.id, { src: url });
    }
  }, [element.id, onUpdate]);

  // Handle file upload from PC
  const handleFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB.');
      return;
    }

    setIsUploading(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result;
      if (dataUrl && onUpdate) {
        onUpdate(element.id, { 
          src: dataUrl,
          alt: alt || file.name.replace(/\.[^/.]+$/, '') // Use filename as alt if none set
        });
      }
      setIsUploading(false);
    };
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
      setIsUploading(false);
    };
    reader.readAsDataURL(file);
  }, [element.id, alt, onUpdate]);

  // Trigger file input click
  const handleUploadClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  }, []);

  // Proper event handlers for Slate
  const handleSettingsClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(!showSettings);
  }, [showSettings]);

  const handleDeleteClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete(element.id);
    }
  }, [element.id, onDelete]);

  const handleCloseSettings = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSettings(false);
  }, []);

  // Alignment styles
  const containerStyle = {
    display: 'flex',
    justifyContent: alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center',
  };

  return (
    <div 
      {...attributes}
      contentEditable={false}
      className={`static-image-element relative my-4 rounded-lg border-2 transition-colors ${
        selected && focused ? 'border-blue-500 shadow-lg' : 'border-transparent hover:border-gray-300'
      }`}
    >
      {children}
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Controls - visible when selected or on hover */}
      <div 
        className={`absolute top-2 right-2 flex items-center gap-2 z-10 transition-opacity ${
          selected ? 'opacity-100' : 'opacity-0 hover:opacity-100'
        }`}
        contentEditable={false}
      >
        <button
          ref={settingsButtonRef}
          type="button"
          onMouseDown={handleSettingsClick}
          className="image-settings-button p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 cursor-pointer"
          title="Settings"
        >
          <Settings size={16} />
        </button>
        {onDelete && (
          <button
            type="button"
            onMouseDown={handleDeleteClick}
            className="p-2 bg-white rounded-lg shadow-md hover:bg-red-100 cursor-pointer"
            title="Delete"
          >
            <X size={16} className="text-red-500" />
          </button>
        )}
      </div>

      {/* Settings Panel - rendered via portal */}
      {showSettings && (
        <SettingsPortal buttonRef={settingsButtonRef}>
          <div 
            className="image-settings-panel bg-white rounded-lg shadow-2xl border p-4 min-w-[350px] max-h-[500px] overflow-y-auto"
            contentEditable={false}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold">Image Settings</h4>
              <button
                type="button"
                onMouseDown={handleCloseSettings}
                className="p-1 hover:bg-gray-100 rounded cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b mb-4">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setActiveTab('gallery'); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                  activeTab === 'gallery' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Image size={16} />
                Gallery
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setActiveTab('upload'); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                  activeTab === 'upload' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Upload size={16} />
                Upload
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setActiveTab('url'); }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                  activeTab === 'url' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Link size={16} />
                URL
              </button>
            </div>

            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(STATIC_IMAGES).map(([key, { label, src: imgSrc }]) => (
                    <button
                      key={key}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleImageSelect(key);
                      }}
                      className={`p-2 border rounded-lg hover:bg-blue-50 text-left cursor-pointer transition-colors ${
                        element.src === key ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <img 
                        src={imgSrc} 
                        alt={label} 
                        className="w-full h-16 object-cover rounded mb-1"
                      />
                      <span className="text-xs text-gray-600 line-clamp-1">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Tab */}
            {activeTab === 'upload' && (
              <div className="mb-4">
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
                  onMouseDown={handleUploadClick}
                >
                  {isUploading ? (
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                      <p className="text-gray-600">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} className="mx-auto text-gray-400 mb-3" />
                      <p className="text-gray-600 font-medium mb-1">Click to upload image</p>
                      <p className="text-gray-400 text-sm">PNG, JPG, GIF up to 5MB</p>
                    </>
                  )}
                </div>
                
                {/* Show current uploaded image if it's a data URL */}
                {src && src.startsWith('data:') && (
                  <div className="mt-3 p-2 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Current uploaded image:</p>
                    <img 
                      src={src} 
                      alt="Uploaded" 
                      className="max-h-24 mx-auto rounded"
                    />
                  </div>
                )}
              </div>
            )}

            {/* URL Tab */}
            {activeTab === 'url' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                <input
                  type="text"
                  value={!STATIC_IMAGES[src] && !src?.startsWith('data:') ? src || '' : ''}
                  onChange={(e) => handleCustomUrlChange(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">Enter a direct link to an image</p>
              </div>
            )}

            {/* Common Settings */}
            <div className="border-t pt-4 mt-4">
              {/* Alt Text */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Alt Text</label>
                <input
                  type="text"
                  value={alt}
                  onChange={(e) => handleAltChange(e.target.value)}
                  placeholder="Image description for accessibility"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>

              {/* Width */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Width</label>
                <select
                  value={width}
                  onChange={(e) => handleWidthChange(e.target.value)}
                  className="w-full border rounded-md px-3 py-2 text-sm"
                >
                  <option value="25%">25%</option>
                  <option value="50%">50%</option>
                  <option value="75%">75%</option>
                  <option value="100%">100%</option>
                  <option value="auto">Auto</option>
                </select>
              </div>

              {/* Alignment */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
                <div className="flex gap-2">
                  {['left', 'center', 'right'].map((align) => (
                    <button
                      key={align}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAlignmentChange(align);
                      }}
                      className={`flex-1 px-3 py-2 border rounded-md text-sm capitalize cursor-pointer transition-colors ${
                        alignment === align 
                          ? 'bg-blue-100 border-blue-500 text-blue-700' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {align}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </SettingsPortal>
      )}

      {/* Image Display */}
      <div className="p-4" style={containerStyle}>
        {imageSrc ? (
          <img 
            src={imageSrc} 
            alt={alt}
            style={{ width, maxWidth: '100%' }}
            className="rounded-lg"
          />
        ) : (
          <div 
            className="image-placeholder flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg text-center w-full cursor-pointer hover:border-blue-400 transition-colors"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowSettings(true);
            }}
          >
            <Image size={48} className="text-gray-400 mb-4" />
            <p className="text-gray-500 mb-2">No image selected</p>
            <p className="text-gray-400 text-sm mb-4">Click to add an image from gallery, upload, or URL</p>
            <button
              type="button"
              onMouseDown={handleUploadClick}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer flex items-center gap-2"
            >
              <Upload size={16} />
              Upload from PC
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StaticImageElement;
